/**
 * OpenMeta — Cartes de blocs (éditeur + preview) — Phase 9.
 * 3 types : content, chatbot, form.
 * Tout le contenu est rendu via React (JSX escape par défaut) — jamais dangerouslySetInnerHTML.
 */
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import {
  MessageSquare, ClipboardList, FileText, Trash2, ChevronUp, ChevronDown,
  Plus, X, Send, Bot, User as UserIcon, Loader2, CheckCircle2,
} from 'lucide-react';
import { sendPublicChat, submitPublicForm } from '@/lib/apps-api';

const FIELD_TYPES = ['text', 'email', 'textarea', 'select'];

// ---------- Éditeur (utilisé dans /studio/app/:id) ----------
export function BlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, disableUp, disableDown, index, accent }) {
  const t = block.type;
  const Icon = t === 'chatbot' ? MessageSquare : t === 'form' ? ClipboardList : FileText;
  return (
    <div
      className="relative rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5"
      data-testid={`block-editor-${index}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="grid h-8 w-8 place-items-center rounded-full"
          style={{ background: `${accent}18`, color: accent }}
        >
          <Icon size={15} strokeWidth={2.4} />
        </span>
        <span className="text-xs font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
          {t === 'chatbot' ? 'Chatbot IA' : t === 'form' ? 'Formulaire' : 'Page contenu'}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={disableUp}
            title="Monter"
            data-testid={`block-move-up-${index}`}
            className="grid h-7 w-7 place-items-center rounded-full text-[var(--om-muted)] hover:bg-[var(--om-bg)] hover:text-[var(--om-ink)] disabled:opacity-30"
          >
            <ChevronUp size={14} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={disableDown}
            title="Descendre"
            data-testid={`block-move-down-${index}`}
            className="grid h-7 w-7 place-items-center rounded-full text-[var(--om-muted)] hover:bg-[var(--om-bg)] hover:text-[var(--om-ink)] disabled:opacity-30"
          >
            <ChevronDown size={14} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            title="Supprimer"
            data-testid={`block-remove-${index}`}
            className="grid h-7 w-7 place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"
          >
            <Trash2 size={14} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <div className="mt-4">
        {t === 'chatbot' && <ChatbotEditor cfg={block.config} onChange={onChange} testidPrefix={`block-${index}`} />}
        {t === 'form' && <FormFieldsEditor cfg={block.config} onChange={onChange} testidPrefix={`block-${index}`} />}
        {t === 'content' && <ContentEditor cfg={block.config} onChange={onChange} testidPrefix={`block-${index}`} />}
      </div>
    </div>
  );
}

function LabeledField({ label, hint, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[10px] text-[var(--om-muted-2)]">{hint}</div>}
    </label>
  );
}

const inputCls =
  'w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2 text-sm text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] focus:border-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10';

function ChatbotEditor({ cfg, onChange, testidPrefix }) {
  return (
    <div className="space-y-3">
      <LabeledField label="Nom de l'assistant">
        <input
          type="text"
          className={inputCls}
          value={cfg.bot_name || ''}
          onChange={(e) => onChange({ bot_name: e.target.value })}
          maxLength={40}
          data-testid={`${testidPrefix}-chatbot-name`}
        />
      </LabeledField>
      <LabeledField label="Message d'accueil">
        <textarea
          className={inputCls + ' min-h-[72px]'}
          value={cfg.greeting || ''}
          onChange={(e) => onChange({ greeting: e.target.value })}
          maxLength={300}
          data-testid={`${testidPrefix}-chatbot-greeting`}
        />
      </LabeledField>
      <LabeledField
        label="Persona / instructions"
        hint="Comportement, rôle, garde-fous. Ne mettez pas de données confidentielles."
      >
        <textarea
          className={inputCls + ' min-h-[120px]'}
          value={cfg.persona || ''}
          onChange={(e) => onChange({ persona: e.target.value })}
          maxLength={1500}
          data-testid={`${testidPrefix}-chatbot-persona`}
        />
      </LabeledField>
    </div>
  );
}

function ContentEditor({ cfg, onChange, testidPrefix }) {
  const paras = Array.isArray(cfg.paragraphs) ? cfg.paragraphs : [''];
  const setParas = (next) => onChange({ paragraphs: next });
  return (
    <div className="space-y-3">
      <LabeledField label="Titre de la page">
        <input
          type="text"
          className={inputCls}
          value={cfg.title || ''}
          onChange={(e) => onChange({ title: e.target.value })}
          maxLength={120}
          data-testid={`${testidPrefix}-content-title`}
        />
      </LabeledField>
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Paragraphes</div>
        <div className="space-y-2">
          {paras.map((p, i) => (
            <div key={i} className="flex items-start gap-2">
              <textarea
                className={inputCls + ' min-h-[80px]'}
                value={p}
                onChange={(e) => {
                  const next = paras.slice();
                  next[i] = e.target.value;
                  setParas(next);
                }}
                maxLength={1500}
                data-testid={`${testidPrefix}-content-para-${i}`}
              />
              <button
                type="button"
                onClick={() => setParas(paras.filter((_, k) => k !== i))}
                className="grid h-8 w-8 place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"
                aria-label="Retirer paragraphe"
                data-testid={`${testidPrefix}-content-para-remove-${i}`}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setParas([...paras, ''])}
          disabled={paras.length >= 8}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`${testidPrefix}-content-para-add`}
        >
          <Plus size={11} strokeWidth={2.4} /> Ajouter un paragraphe
        </button>
      </div>
    </div>
  );
}

function FormFieldsEditor({ cfg, onChange, testidPrefix }) {
  const fields = Array.isArray(cfg.fields) ? cfg.fields : [];
  const setFields = (next) => onChange({ fields: next });
  const patchField = (i, patch) => {
    const next = fields.slice();
    next[i] = { ...next[i], ...patch };
    setFields(next);
  };
  return (
    <div className="space-y-3">
      <LabeledField label="Titre du formulaire">
        <input
          type="text"
          className={inputCls}
          value={cfg.title || ''}
          onChange={(e) => onChange({ title: e.target.value })}
          maxLength={120}
          data-testid={`${testidPrefix}-form-title`}
        />
      </LabeledField>
      <LabeledField label="Message de confirmation">
        <input
          type="text"
          className={inputCls}
          value={cfg.success_message || ''}
          onChange={(e) => onChange({ success_message: e.target.value })}
          maxLength={300}
          data-testid={`${testidPrefix}-form-success`}
        />
      </LabeledField>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Champs ({fields.length})</div>
          <button
            type="button"
            onClick={() => setFields([...fields, { id: `field_${Math.random().toString(36).slice(2, 8)}`, label: 'Nouveau champ', type: 'text', required: false }])}
            disabled={fields.length >= 8}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm disabled:opacity-50"
            data-testid={`${testidPrefix}-form-field-add`}
          >
            <Plus size={11} strokeWidth={2.4} /> Ajouter un champ
          </button>
        </div>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={i} className="rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] p-3" data-testid={`${testidPrefix}-form-field-${i}`}>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Libellé"
                  className={inputCls + ' text-xs'}
                  value={f.label || ''}
                  onChange={(e) => patchField(i, { label: e.target.value })}
                  maxLength={80}
                  data-testid={`${testidPrefix}-form-field-${i}-label`}
                />
                <select
                  className={inputCls + ' text-xs'}
                  value={f.type}
                  onChange={(e) => patchField(i, { type: e.target.value })}
                  data-testid={`${testidPrefix}-form-field-${i}-type`}
                >
                  {FIELD_TYPES.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
                </select>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs text-[var(--om-muted)]">
                  <input
                    type="checkbox"
                    checked={!!f.required}
                    onChange={(e) => patchField(i, { required: e.target.checked })}
                    data-testid={`${testidPrefix}-form-field-${i}-required`}
                  />
                  Requis
                </label>
                {f.type === 'select' && (
                  <input
                    type="text"
                    placeholder="Options séparées par ;"
                    className={inputCls + ' flex-1 text-xs'}
                    value={(f.options || []).join(';')}
                    onChange={(e) => patchField(i, { options: e.target.value.split(';').map(s => s.trim()).filter(Boolean) })}
                    data-testid={`${testidPrefix}-form-field-${i}-options`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setFields(fields.filter((_, k) => k !== i))}
                  className="grid h-7 w-7 place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"
                  aria-label="Retirer champ"
                  data-testid={`${testidPrefix}-form-field-${i}-remove`}
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
          {fields.length === 0 && (
            <div className="rounded-xl border border-dashed border-[var(--om-border)] bg-[var(--om-bg)] p-4 text-center text-xs text-[var(--om-muted-2)]">
              Ajoutez au moins un champ.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Rendu bloc public (utilisé dans /app/:slug et preview) ----------
export function BlockPublicRenderer({ block, slug, accent, lang = 'fr', preview = false }) {
  const t = block.type;
  if (t === 'content') return <ContentBlockView block={block} />;
  if (t === 'chatbot') return <ChatbotBlockView block={block} slug={slug} accent={accent} lang={lang} preview={preview} />;
  if (t === 'form') return <FormBlockView block={block} slug={slug} accent={accent} lang={lang} preview={preview} />;
  return null;
}

function ContentBlockView({ block }) {
  const cfg = block.config || {};
  const paras = Array.isArray(cfg.paragraphs) ? cfg.paragraphs : [];
  return (
    <section className="rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-6 md:p-8" data-testid={`public-block-content-${block.id}`}>
      {cfg.title && <h2 className="text-2xl font-extrabold text-[var(--om-ink)]">{cfg.title}</h2>}
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-[var(--om-ink-soft)]">
        {paras.map((p, i) => (
          <p key={i} className="whitespace-pre-wrap">{p}</p>
        ))}
      </div>
    </section>
  );
}

function ChatbotBlockView({ block, slug, accent, lang, preview }) {
  const cfg = block.config || {};
  const [messages, setMessages] = useState(() => (
    cfg.greeting ? [{ role: 'assistant', content: cfg.greeting }] : []
  ));
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Reset greeting quand cfg change (preview live)
    if (preview) {
      setMessages(cfg.greeting ? [{ role: 'assistant', content: cfg.greeting }] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.greeting, preview]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    if (preview) {
      toast(lang === 'en' ? 'Chatbot preview: publish to test live.' : 'Aperçu chatbot : publiez pour le tester en réel.', { icon: 'ℹ️' });
      return;
    }
    setInput('');
    const nextHistory = [...messages, { role: 'user', content: text }];
    setMessages(nextHistory);
    setSending(true);
    // Envoyer max 10 derniers messages (fenêtre)
    const payload = nextHistory.slice(-10);
    const res = await sendPublicChat(slug, payload);
    setSending(false);
    if (res.ok) {
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } else if (res.code === 'rate_limit') {
      toast.error(lang === 'en' ? 'Too many messages — please slow down.' : 'Trop de messages — patientez un instant.');
    } else if (res.code === 'unavailable') {
      toast.error(lang === 'en' ? 'The assistant is momentarily unavailable.' : "L'assistant est momentanément indisponible.");
    } else {
      toast.error(lang === 'en' ? 'Chat error.' : 'Erreur du chat.');
    }
  };

  return (
    <section
      className="overflow-hidden rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)]"
      data-testid={`public-block-chatbot-${block.id}`}
    >
      <header
        className="flex items-center gap-3 border-b border-[var(--om-border)] px-6 py-4"
        style={{ background: `${accent}0F` }}
      >
        <span
          className="grid h-9 w-9 place-items-center rounded-full text-white"
          style={{ background: accent }}
        >
          <Bot size={16} strokeWidth={2.4} />
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-[var(--om-ink)]">{cfg.bot_name || 'Assistant'}</div>
          <div className="text-[11px] text-[var(--om-muted)]">
            {preview ? (lang === 'en' ? 'Preview mode' : 'Mode aperçu') : (lang === 'en' ? 'Powered by AI' : 'Propulsé par IA')}
          </div>
        </div>
      </header>
      <div ref={scrollRef} className="max-h-[360px] space-y-3 overflow-y-auto px-6 py-5">
        {messages.map((m, i) => (
          <div key={i} className={`flex items-start gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <span
              className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-white`}
              style={{ background: m.role === 'user' ? '#0A0E1A' : accent }}
              aria-hidden
            >
              {m.role === 'user' ? <UserIcon size={12} strokeWidth={2.4} /> : <Bot size={12} strokeWidth={2.4} />}
            </span>
            <div
              className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                m.role === 'user' ? 'text-white' : 'bg-[var(--om-bg)] text-[var(--om-ink)]'
              }`}
              style={m.role === 'user' ? { background: '#0A0E1A' } : undefined}
              data-testid={`chatbot-msg-${i}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-[var(--om-muted)]" data-testid="chatbot-sending">
            <Loader2 size={12} className="animate-spin" /> {lang === 'en' ? 'Thinking…' : 'Réflexion…'}
          </div>
        )}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-[var(--om-border)] px-4 py-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={lang === 'en' ? 'Type your message…' : 'Écrivez votre message…'}
          className={inputCls + ' flex-1'}
          maxLength={800}
          data-testid={`chatbot-input-${block.id}`}
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white disabled:cursor-not-allowed disabled:opacity-50"
          style={{ background: accent }}
          data-testid={`chatbot-send-${block.id}`}
          aria-label={lang === 'en' ? 'Send' : 'Envoyer'}
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.4} />}
        </button>
      </form>
    </section>
  );
}

function FormBlockView({ block, slug, accent, lang, preview }) {
  const cfg = block.config || {};
  const fields = Array.isArray(cfg.fields) ? cfg.fields : [];
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (preview) {
      toast(lang === 'en' ? 'Form preview: publish to record real entries.' : 'Aperçu formulaire : publiez pour enregistrer.', { icon: 'ℹ️' });
      return;
    }
    setSubmitting(true);
    const res = await submitPublicForm(slug, block.id, values);
    setSubmitting(false);
    if (res.ok) {
      setDone(true);
      setValues({});
    } else if (res.code === 'invalid') {
      toast.error(res.detail || (lang === 'en' ? 'Some fields are invalid.' : 'Certains champs sont invalides.'));
    } else if (res.code === 'rate_limit') {
      toast.error(lang === 'en' ? 'Too many submissions — try later.' : 'Trop de soumissions — réessayez plus tard.');
    } else {
      toast.error(lang === 'en' ? 'Submission error.' : "Échec de l'envoi.");
    }
  };

  return (
    <section className="rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-6 md:p-8" data-testid={`public-block-form-${block.id}`}>
      {cfg.title && <h2 className="text-2xl font-extrabold text-[var(--om-ink)]">{cfg.title}</h2>}
      {done ? (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-5 py-4 text-sm text-emerald-700" data-testid={`public-block-form-success-${block.id}`}>
          <CheckCircle2 size={18} strokeWidth={2.4} />
          <span>{cfg.success_message || (lang === 'en' ? 'Thanks!' : 'Merci !')}</span>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
          {fields.map((f) => (
            <label key={f.id} className="block">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                {f.label}{f.required ? ' *' : ''}
              </div>
              {f.type === 'textarea' ? (
                <textarea
                  className={inputCls + ' min-h-[100px]'}
                  value={values[f.id] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                  required={f.required}
                  placeholder={f.placeholder || ''}
                  data-testid={`public-form-field-${f.id}`}
                />
              ) : f.type === 'select' ? (
                <select
                  className={inputCls}
                  value={values[f.id] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                  required={f.required}
                  data-testid={`public-form-field-${f.id}`}
                >
                  <option value="">—</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={f.type === 'email' ? 'email' : 'text'}
                  className={inputCls}
                  value={values[f.id] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                  required={f.required}
                  placeholder={f.placeholder || ''}
                  data-testid={`public-form-field-${f.id}`}
                />
              )}
            </label>
          ))}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: accent }}
            data-testid={`public-form-submit-${block.id}`}
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? (lang === 'en' ? 'Sending…' : 'Envoi…') : (lang === 'en' ? 'Send' : 'Envoyer')}
          </button>
        </form>
      )}
    </section>
  );
}
